import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { CarbonMark } from './CarbonMark.js'

/**
 * The frame the privacy notice and the terms sit in.
 *
 * Deliberately not `AuthLayout`. That layout's second column is the only
 * marketing in the product, and a page someone opens to find out what happens
 * to their data is the one page where a persuasion panel beside the text would
 * be working against the reader.
 *
 * Public and reachable without a session, because both of these have to be
 * readable by people who will never have an account: a recipient who wants to
 * know who holds their address, and Google's OAuth reviewer, who requires a
 * privacy policy on a URL that does not ask for a login.
 *
 * One narrow column. Measure is the whole design job on a page of prose.
 */
export function LegalLayout({
  title,
  updated,
  children,
}: {
  readonly title: string
  readonly updated: string
  readonly children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-bg">
      <header className="border-line border-b bg-surface">
        <div className="mx-auto flex max-w-[720px] items-center gap-2.5 px-6 py-5">
          <CarbonMark className="h-[30px] w-[26px]" />
          <span className="font-bold text-[17px] text-t1 -tracking-[0.01em]">Carbon Mail</span>
        </div>
      </header>

      <main className="mx-auto max-w-[720px] px-6 py-12">
        <h1 className="font-bold text-[28px] text-t1 -tracking-[0.01em]">{title}</h1>
        <p className="mt-2 text-[13px] text-t3">Last updated {updated}</p>

        <div className="mt-9 flex flex-col gap-8">{children}</div>

        <nav className="mt-12 flex gap-5 border-line border-t pt-6 text-[13px]">
          <Link className="font-medium text-t3 hover:text-t1" to="/privacy">
            Privacy
          </Link>
          <Link className="font-medium text-t3 hover:text-t1" to="/terms">
            Terms
          </Link>
          <Link className="font-medium text-t3 hover:text-t1" to="/signin">
            Sign in
          </Link>
        </nav>
      </main>
    </div>
  )
}

/** A heading and its prose. Both pages are nothing but these. */
export function LegalSection({
  heading,
  children,
}: {
  readonly heading: string
  readonly children: ReactNode
}) {
  return (
    <section>
      <h2 className="mb-2.5 font-bold text-[17px] text-t1 -tracking-[0.01em]">{heading}</h2>
      <div className="flex flex-col gap-3 text-[14px] text-t2 leading-relaxed">{children}</div>
    </section>
  )
}

/** A list of plain statements, used where prose would become a wall. */
export function LegalList({ children }: { readonly children: ReactNode }) {
  return <ul className="flex list-disc flex-col gap-2 pl-5">{children}</ul>
}
